import React, { useCallback, useEffect, useState } from 'react'

import { PageTitle } from '../../../../../_metronic/layout/core'
import { KTIcon } from '../../../../../_metronic/helpers'
import {
  SortableTableBody,
  SortableTableRow,
  Table,
  useRowReorder,
} from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { getErrorCode } from '../../../../common/api/errors'
import { convertUTCtoMSK } from '../../../../../utils'

import { getReasons, reorderReasons } from './core/api'
import { OverpaymentReason } from './core/response.types'
import { OVERPAYMENT_REASONS_BREADCRUMBS } from './core/consts'
import { ReasonModal } from './components/ReasonModal'

const getRowId = (reason: OverpaymentReason) => reason.id

export const OverpaymentReasonsPage: React.FC = () => {
  const [items, setItems] = useState<OverpaymentReason[]>([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<OverpaymentReason | null>(null)
  const [saving, setSaving] = useState(false)
  const [orderError, setOrderError] = useState<string | null>(null)

  const reorder = useRowReorder(items, getRowId)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await getReasons({ page: 1, perPage: 200 })
      if (r.data.success) {
        setItems(r.data.data ?? [])
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openCreate = () => {
    setEditing(null)
    setShowModal(true)
  }

  const openEdit = (reason: OverpaymentReason) => {
    setEditing(reason)
    setShowModal(true)
  }

  const startReorder = () => {
    setOrderError(null)
    reorder.open()
  }

  const cancelReorder = () => {
    setOrderError(null)
    reorder.exit()
  }

  const saveOrder = async () => {
    setSaving(true)
    setOrderError(null)
    try {
      const r = await reorderReasons(reorder.ids)
      if (!r.data.success) {
        setOrderError('Не удалось сохранить порядок')
        return
      }
      // Reload before leaving the mode: the draft keeps the operator's arrangement on screen until
      // the confirmed order has arrived, so the table never flashes the old order in between.
      await load()
      reorder.exit()
    } catch (e: unknown) {
      // Порядок пишется целиком: если справочник изменился, бэкенд отвергает запрос, а не
      // применяет половину. Оставляем расстановку на экране, чтобы работа не пропала.
      setOrderError(
        getErrorCode(e) === 'REORDER_SET_MISMATCH'
          ? 'Список изменился — обновите страницу'
          : 'Не удалось сохранить порядок'
      )
    } finally {
      setSaving(false)
    }
  }

  const nextSortOrder = items.reduce((max, r) => Math.max(max, r.sortOrder), 0) + 1

  return (
    <>
      <PageTitle breadcrumbs={OVERPAYMENT_REASONS_BREADCRUMBS as any}>Причины переоплат</PageTitle>
      <div className='card'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h3 className='card-title'>Справочник причин переоплат</h3>
          <div className='d-flex gap-2'>
            {reorder.active ? (
              <>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={cancelReorder}
                  disabled={saving}
                >
                  Отмена
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-success'
                  onClick={saveOrder}
                  disabled={saving || !reorder.dirty}
                >
                  {saving ? 'Сохраняем…' : 'Сохранить'}
                </button>
              </>
            ) : (
              <>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={startReorder}
                  disabled={items.length < 2}
                >
                  Изменить порядок
                </button>
                <button type='button' className='btn btn-sm btn-primary' onClick={openCreate}>
                  Добавить
                </button>
              </>
            )}
          </div>
        </div>
        <div className='card-body'>
          {orderError && <div className='alert alert-danger py-2'>{orderError}</div>}
          {loading ? (
            <div className='text-muted'>Загрузка…</div>
          ) : items.length === 0 ? (
            <div className='text-muted'>Записей нет</div>
          ) : (
            <Table>
              <TableHead>
                <tr>
                  {reorder.active && <th style={{ width: 40 }} />}
                  <th style={{ width: 50 }}>#</th>
                  <th>Код</th>
                  <th>Название</th>
                  <th>Активна</th>
                  <th>Создано</th>
                  <th />
                </tr>
              </TableHead>
              <SortableTableBody ids={reorder.ids} disabled={!reorder.active} onMove={reorder.move}>
                {reorder.rows.map((r, index) => (
                  <SortableTableRow key={r.id} id={r.id} disabled={!reorder.active}>
                    {({ attributes, listeners }) => (
                      <>
                        {reorder.active && (
                          <td style={{ width: 40 }}>
                            <span
                              title='Переместить'
                              className='text-gray-400'
                              style={{ cursor: 'grab' }}
                              {...attributes}
                              {...listeners}
                            >
                              <KTIcon iconName='abstract-14' className='fs-2' />
                            </span>
                          </td>
                        )}
                        <td className='fw-bold text-gray-600'>{index + 1}</td>
                        <td className='text-monospace'>{r.code}</td>
                        <td>{r.title}</td>
                        <td>{r.enabled ? 'Да' : '—'}</td>
                        <td>{convertUTCtoMSK(r.createdAt)}</td>
                        <td className='text-end'>
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={() => openEdit(r)}
                            disabled={reorder.active}
                          >
                            Изменить
                          </button>
                        </td>
                      </>
                    )}
                  </SortableTableRow>
                ))}
              </SortableTableBody>
            </Table>
          )}
        </div>
      </div>
      <ReasonModal
        show={showModal}
        onClose={() => setShowModal(false)}
        editing={editing}
        nextSortOrder={nextSortOrder}
        onSaved={() => load()}
      />
    </>
  )
}

export default OverpaymentReasonsPage
